import React, { useContext, useEffect, useRef, useState } from "react";
import Modal from "../components/common/Modal/Modal";
import Button from "../components/common/Button";
import { Stack, useTheme } from "@mui/system";
import { Typography } from "@mui/material";
import CheckBox from "../components/common/CheckBox/CheckBox";

type UseModalShowReturnType = {
    show: boolean;
    setShow: (value: boolean) => void;
    onHide: () => void;
}

const useModalShow = (): UseModalShowReturnType => {
    const [show, setShow] = useState(false);

    const handleOnHide = () => {
        setShow(false);
    };

    return {
        show,
        setShow,
        onHide: handleOnHide
    }
};

export interface ConfirmDontShowAgain {
    result: boolean,
    dontShowAgain: boolean
}

type ModalContextType = {
    showConfirmation: (
        title: string,
        message: string | React.JSX.Element,
        confirmTitle?: string,
        cancelTitle?: string,
        showConfirm?: boolean,
        showCancel?: boolean,
        showAgainOption?: boolean
    ) => Promise<boolean | ConfirmDontShowAgain>;
};

type ConfirmationModalContextProviderProps = {
    children: React.ReactNode
}

const ConfirmationModalContext = React.createContext<ModalContextType>({} as ModalContextType);

const ConfirmationModalContextProvider: React.FC<ConfirmationModalContextProviderProps> = (props) => {
    const { setShow, show, onHide } = useModalShow();
    const [content, setContent] = useState<{
        title: string,
        message: string | React.JSX.Element,
        confirmTitle?: string,
        cancelTitle?: string,
        showConfirm?: boolean,
        showCancel?: boolean,
        showAgainOption?: boolean
    } | null>();
    const [dontShowAgain, setDontShowAgain] = useState<boolean>(false);
    const resolver = useRef<Function>(undefined);
    const theme = useTheme();

    const handleShow = (
        title: string,
        message: string | React.JSX.Element,
        confirmTitle: string = 'Ok',
        cancelTitle: string = 'Cancel',
        showConfirm: boolean = true,
        showCancel: boolean = true,
        showAgainOption: boolean = false
    ): Promise<boolean | ConfirmDontShowAgain> => {
        setContent({
            title,
            message,
            confirmTitle,
            cancelTitle,
            showConfirm,
            showCancel,
            showAgainOption
        });
        setShow(true);
        return new Promise(function (resolve) {
            resolver.current = resolve;
        });
    };

    const modalContext: ModalContextType = {
        showConfirmation: handleShow
    };

    const handleOk = () => {
        resolver.current && resolver.current(content?.showAgainOption
            ? { result: true, dontShowAgain: dontShowAgain }
            : true);
        onHide();
    };

    const handleCancel = () => {
        resolver.current && resolver.current(content?.showAgainOption
            ? { result: false, dontShowAgain: dontShowAgain }
            : false);
        onHide();
    };

    useEffect(() => {
        // Handle window refreshing closing unexpected
        return () => {
            resolver.current && resolver.current(content?.showAgainOption
                ? { result: false, dontShowAgain: false }
                : false);
            onHide();
        }
    }, [])

    return (
        <ConfirmationModalContext.Provider value={modalContext}>
            {props.children}
            {content &&
                (show ? (
                    <Modal title={content.title} closeModal={handleCancel}>
                        <Stack direction="column">
                            <Stack
                                justifyContent="center"
                                sx={{
                                    marginTop: '24px',
                                    marginBottom: '48px'
                                }}>
                                {(React.isValidElement(content.message)) &&
                                    <>{content.message}</>
                                }
                                {(!React.isValidElement(content.message)) &&
                                    <Typography>{content.message}</Typography>
                                }
                                {content.showAgainOption &&
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        marginTop="24px">
                                        <CheckBox onChange={(event) => setDontShowAgain(event.target.checked)} />
                                        <Typography>Don't show this message again</Typography>
                                    </Stack>
                                }
                            </Stack>
                            <Stack
                                direction="row"
                                justifyContent="flex-end"
                                spacing={2}
                                useFlexGap
                                sx={{
                                    '& .MuiButtonBase-root': {
                                        minWidth: '100px'
                                    },
                                    [theme.breakpoints.down('mobile')]: {
                                        flexDirection: "column",
                                    }
                                }}>
                                {content.showConfirm &&
                                    <Button
                                        onClick={handleOk}>
                                        {content.confirmTitle}
                                    </Button>
                                }
                                {content.showCancel &&
                                    <Button
                                        onClick={handleCancel}>
                                        {content.cancelTitle}
                                    </Button>
                                }
                            </Stack>
                        </Stack>
                    </Modal>
                ) : null)
            }
        </ConfirmationModalContext.Provider>
    )
};

const useConfirmationModalContext = (): ModalContextType => useContext(ConfirmationModalContext);

export { useModalShow, useConfirmationModalContext }

export default ConfirmationModalContextProvider;